import React, { useState, useContext } from 'react'
import { useHistory } from 'react-router'
import { Formik, FormikProps } from 'formik'
import * as yup from 'yup'

// Constants
import { routes } from '@sketch/constants'

// Containers
import { ModalContext, ModalInjectedProps } from 'containers/ModalManager'

// Components
import { Button, Checkbox, Form, Input } from '@sketch/components'
import Modal from 'components/Modal'
import { SelectOption } from 'components/Select'

// Local Components
import Divider from '../../components/Divider'
import {
  allScopes,
  descriptionForScope,
  PublicApiAccessScope,
} from '../../components/AccessScope'
import CopyTokenModal from '../CopyTokenModal'

// Styles
import {
  CheckboxGroup,
  ExpiryInputContainer,
  HumanReadableExpiry,
  StyledModal,
  StyledSelect,
} from './CreateTokenModal.styles'

// Utils
import { dateFormat } from '@sketch/utils'

// Hooks
import { useCreatePersonalToken } from '../../operations'

const INITIAL_VALUES = {
  description: '',
  accessScopes: [],
  expiresAt: undefined,
  expiryDropdown: undefined,
}

type Values = typeof INITIAL_VALUES

const tomorrow = new Date()
tomorrow.setDate(tomorrow.getDate() + 1)
tomorrow.setHours(0)
tomorrow.setMinutes(0)
tomorrow.setSeconds(0)
tomorrow.setMilliseconds(0)
const minExpiresAt = tomorrow
const maxExpiresAt = new Date('9999-12-31')

const VALIDATION_SCHEMA = yup.object().shape({
  description: yup
    .string()
    .required('Enter a token description')
    .max(255, 'Maximum 255 characters'),
  expiresAt: yup
    .date()
    .nullable()
    .test('is selected', 'Select an expiry date', value => {
      return value !== undefined
    })
    .min(minExpiresAt, 'Select a future date')
    .max(maxExpiresAt, 'Select a future date (max 9999-12-31)'),
  accessScopes: yup
    .array()
    .of(yup.string())
    .min(1, 'Select one or more options'),
})

const DescriptionInput = ({
  touched,
  errors,
  values,
  handleChange,
  handleBlur,
}: FormikProps<Values>) => (
  <Form.Field
    name="description"
    label="Token Description"
    errorText={touched.description ? errors.description : undefined}
  >
    <Input
      type="text"
      name="description"
      placeholder="Enter a description..."
      maxLength={255}
      value={values.description}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  </Form.Field>
)

type AccessScopesCheckboxProps = {
  value: PublicApiAccessScope
  label: string
  checked: boolean
  onChange: FormikProps<Values>['handleChange']
}

const AccessScopeCheckbox = (props: AccessScopesCheckboxProps) => {
  const { label, ...inputProps } = props
  return (
    <Form.Field name="accessScopes">
      <Checkbox name="accessScopes" {...inputProps}>
        {label}
      </Checkbox>
    </Form.Field>
  )
}

const AccessScopesCheckboxes = (props: FormikProps<Values>) => {
  const {
    accessScopes,
  }: {
    accessScopes: PublicApiAccessScope[]
  } = props.values
  const checkboxes = allScopes.map(scope => {
    const checked = accessScopes.includes(scope)
    const description = descriptionForScope(scope)
    return (
      <AccessScopeCheckbox
        data-testid={`access-scope-checkbox-${scope}`}
        key={scope}
        value={scope}
        checked={checked}
        label={description}
        onChange={props.handleChange}
      />
    )
  })

  return (
    <>
      <div id="access-scope-checkbox-group">Use this token to:</div>
      <CheckboxGroup role="group" aria-labelledby="access-scope-checkbox-group">
        {checkboxes}
      </CheckboxGroup>
      {props.touched.accessScopes && props.errors.accessScopes && (
        <Form.Error>{props.errors.accessScopes}</Form.Error>
      )}
    </>
  )
}

enum ExpiryOption {
  Never = 'NEVER',
  InSevenDays = 'IN_SEVEN_DAYS',
  InThirtyDays = 'IN_THIRTY_DAYS',
  InNinetyDays = 'IN_NINETY_DAYS',
  CustomDate = 'CUSTOM_DATE',
}

const dateFromExpiryOption = (
  expiryOption: ExpiryOption
): Date | null | undefined => {
  const now = new Date()
  switch (expiryOption) {
    case ExpiryOption.InSevenDays:
      now.setDate(now.getDate() + 7)
      return now
    case ExpiryOption.InThirtyDays:
      now.setDate(now.getDate() + 30)
      return now
    case ExpiryOption.InNinetyDays:
      now.setDate(now.getDate() + 90)
      return now
    case ExpiryOption.Never:
      return null
    case ExpiryOption.CustomDate:
      return undefined
  }
}

const expiryOptionFromString = (
  optionStr: string
): ExpiryOption | undefined => {
  switch (optionStr) {
    case 'NEVER':
      return ExpiryOption.Never
    case 'IN_SEVEN_DAYS':
      return ExpiryOption.InSevenDays
    case 'IN_THIRTY_DAYS':
      return ExpiryOption.InThirtyDays
    case 'IN_NINETY_DAYS':
      return ExpiryOption.InNinetyDays
    case 'CUSTOM_DATE':
      return ExpiryOption.CustomDate
    default:
      return undefined
  }
}

const humanReadableExpiry = (expiryDate: Date | undefined) => {
  if (!expiryDate) return

  const formatted = dateFormat(expiryDate)
  return <HumanReadableExpiry>Expires on {formatted}</HumanReadableExpiry>
}

const ExpiryInput = (props: FormikProps<Values>) => {
  const [expiryOption, setExpiryOption] = useState<ExpiryOption>()

  const onSelectChange = (optionStr: string) => {
    const selectedOption = expiryOptionFromString(optionStr)
    // this should never happen but not sure of a better way to force
    // typescript to accept that the options of the <Select /> are always
    // ExpiryOption
    if (!selectedOption) return

    setExpiryOption(selectedOption)
    if (selectedOption === ExpiryOption.CustomDate) {
      // When the user selects `CustomDate` we set `values.expiresAt` back to
      // undefined. We also set `touched.expiresAt` to false to make sure we
      // don't show an error until they finish interacting with the date picker
      props.setTouched({ expiresAt: false })
    }

    const expiryDate = dateFromExpiryOption(selectedOption)
    const event = { target: { name: 'expiresAt', value: expiryDate } }
    props.handleChange(event)
    props.validateField('expiresAt')
  }

  return (
    <>
      <label>Expires In</label>
      <ExpiryInputContainer data-testid="expiry-container">
        <StyledSelect
          placeholder="Select an option..."
          onChange={onSelectChange}
          value={expiryOption || ''}
        >
          <SelectOption value={ExpiryOption.InSevenDays}>7 days</SelectOption>
          <SelectOption value={ExpiryOption.InThirtyDays}>30 days</SelectOption>
          <SelectOption value={ExpiryOption.InNinetyDays}>90 days</SelectOption>
          <SelectOption value={ExpiryOption.Never}>Never Expires</SelectOption>
          <SelectOption value={ExpiryOption.CustomDate}>Custom...</SelectOption>
        </StyledSelect>
        {expiryOption === ExpiryOption.CustomDate ? (
          <Form.Field name="expiresAt">
            <Input
              min={minExpiresAt.toISOString().slice(0, 10)}
              max={maxExpiresAt.toISOString().slice(0, 10)}
              type="date"
              name="expiresAt"
              value={props.values.expiresAt || ''}
              onChange={props.handleChange}
              onBlur={props.handleBlur}
            />
          </Form.Field>
        ) : (
          humanReadableExpiry(props.values.expiresAt)
        )}
      </ExpiryInputContainer>
      {props.touched.expiresAt && props.errors.expiresAt && (
        <Form.Error>{props.errors.expiresAt}</Form.Error>
      )}
    </>
  )
}

type CreateTokenModalProps = {
  redirectToTokensPage: boolean
} & ModalInjectedProps

const CreateTokenModal: React.FC<CreateTokenModalProps> = props => {
  const { hideModal, redirectToTokensPage } = props
  const { showModal } = useContext(ModalContext)
  const history = useHistory()
  const [loading, setLoading] = useState(false)

  const closeModal = () => {
    if (redirectToTokensPage) {
      history.push(routes.PERSONAL_SETTINGS_ACCESS_TOKENS.create({}))
    } else {
      hideModal()
    }
  }

  const createPersonalToken = useCreatePersonalToken({
    onCompleted: ({ personalToken, secret }) => {
      closeModal()
      showModal(CopyTokenModal, { token: personalToken, secret })
    },
  })

  const handleOnSubmit = async (values: Values) => {
    // <input type="date" /> will only give us a date in the format YYYY-MM-DD
    // whereas the GraphQL schema expects an ISO8601 date-time string that
    // includes the time:
    //   e.g. 2022-03-01T00:00:00.00Z
    //
    // Here we convert the string to a `Date` and then get the full ISO8601
    // string to make sure it includes both the date **and** time.
    // Date.toISOString() gives a string in the following ISO8601 compliant
    // format:
    //   YYYY-MM-DDTHH:mm:ss.sssZ
    const expiresAt = values.expiresAt
      ? new Date(values.expiresAt!).toISOString()
      : null

    setLoading(true)
    await createPersonalToken({
      accessScopes: values.accessScopes,
      description: values.description,
      expiresAt,
    })
    setLoading(false)
  }

  return (
    <StyledModal title="Create a Token" onCancel={closeModal}>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleOnSubmit}
      >
        {formikProps => (
          <Form>
            <Modal.Body>
              <p>
                Now give your token a description, an expiry date and select
                what you want to use it for.
              </p>
              <DescriptionInput {...formikProps} />
              <ExpiryInput {...formikProps} />
              <Divider />
              <AccessScopesCheckboxes {...formikProps} />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={closeModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={loading || Object.keys(formikProps.errors).length > 0}
              >
                Create Token
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </StyledModal>
  )
}

export default CreateTokenModal
